/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, message, Modal } from "antd";
import { PlayerBankFormType } from "api/bank/type";
import InputCustom from "components/base/InputCustom";
import WrapperForm from "components/base/Modal/WrapperForm";
import SelectCustom from "components/base/SelectCustom";
import { ResponseCode } from "constants/response";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import _map from "lodash/map";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { selectUsername } from "store/auth";
import { selectBanks } from "store/bank";
import { addPlayerBank, getListBank } from "store/bank/bank.thunk";
import { setOpenModal } from "store/modal";
import { selectProfile } from "store/user";
import { ModalTypeValue } from "types/modal";
import * as yup from "yup";

import { AddBankStyle } from "./index.style";

export const ModalAddBank = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["common", "modal"]);

  const username = useAppSelector(selectUsername);
  const banks = useAppSelector(selectBanks);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const profile = useAppSelector(selectProfile);

  const schema = yup.object({
    account_no: yup.string().required(t("modal:field_required")),
    account_name: yup.string().required(t("modal:field_required")),
    id: yup.string().required(t("modal:bank_required")),
    username: yup.string().required(t("modal:field_required")),
    mobile: yup.string().required(t("modal:field_required"))
  });

  const listOptionBank = useMemo(
    () =>
      _map(banks, (item) => ({
        label: item.bank_name,
        value: item.bank_id
      })),
    [banks]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<PlayerBankFormType>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      username,
      account_name: String(profile?.full_name),
      account_no: "",
      mobile: String(profile?.mobile)
    }
  });

  useEffect(() => {
    if (!username) return;
    if (banks.length > 0) return;

    dispatch(getListBank(username));
  }, [username, banks.length]);

  useEffect(() => {
    if (profile) {
      setValue("account_name", profile.full_name);
    }
  }, [profile]);

  const onSubmit = async (data: PlayerBankFormType) => {
    setIsLoading(true);

    const res = await dispatch(
      addPlayerBank({ ...data, mobile: String(profile?.mobile) })
    );
    setIsLoading(false);

    const { msg, status } = res.payload as any;

    if (status !== ResponseCode.SUCCESS) {
      message.error({ key: "bank", content: msg });
      return;
    }

    message.success({ key: "bank", content: t("modal:add_bank_success") });
    dispatch(setOpenModal(ModalTypeValue.Close));
    reset();
  };

  return (
    <Modal
      open={true}
      closable={false}
      onCancel={() => dispatch(setOpenModal(ModalTypeValue.Close))}
      css={AddBankStyle.modalContainer}
      footer={<></>}
      centered
    >
      <WrapperForm
        isBank
        title={t("modal:add_bank_title")}
        body={
          <>
            <div>
              <Form
                className="deposit_form"
                onSubmitCapture={handleSubmit(onSubmit)}
              >
                <div
                  css={css`
                    margin: 12px 0;
                  `}
                >
                  <SelectCustom
                    control={control}
                    disabled={isLoading}
                    name={"id"}
                    options={listOptionBank}
                    placeholder={t("modal:select_bank_placeholder")}
                    validate_status={errors.id && "error"}
                    error={errors.id?.message}
                    className="select-custom"
                  />
                </div>

                <InputCustom
                  control={control}
                  name="account_no"
                  errors={errors.account_no?.message}
                  validate_status={errors.account_no && "error"}
                  margin_bottom="12px"
                  placeholder={t("modal:bank_account_number_placeholder")}
                  type="number"
                />

                <InputCustom
                  control={control}
                  name="account_name"
                  errors={errors.account_name?.message}
                  validate_status={errors.account_name && "error"}
                  placeholder={t("modal:bank_account_name_placeholder")}
                  disabled
                  margin_bottom="5px"
                />

                <span className="text-warning small">
                  {t("modal:bank_security_note")}
                </span>

                <div>
                  <Button
                    loading={isLoading}
                    css={css`
                      width: 100%;
                      background-color: #ffb31a;
                      border-color: transparent;
                      height: 38px;
                      font-size: 16px;

                      &:hover {
                        color: black !important;
                        border-color: transparent !important;
                        transform: scale(1.05);
                      }
                    `}
                    htmlType="submit"
                  >
                    {t("modal:add_bank_button")}
                  </Button>
                </div>
              </Form>
            </div>
          </>
        }
      />
    </Modal>
  );
};
